// Capa HTTP del tablero de Feedback. Tipos + funciones fetch; el estado vive
// en TanStack Query (`src/hooks/useFeedback.ts`), mismo patrón que
// `lib/revisiones.ts`.

import { apiFetch } from "./api";

/**
 * Una opción de las listas del formulario (módulos, tipos y estados).
 *
 * Las listas NO se escriben acá: llegan de `GET /api/feedback/catalogos`. El
 * valor se persiste y el servidor lo valida contra un enum, así que una copia
 * local terminaría ofreciendo una opción que el API rechaza con 422 el día
 * que nace una pantalla nueva.
 */
export interface OpcionDeFeedback {
  valor: string;
  etiqueta: string;
}

export interface CatalogosDeFeedback {
  modulos: OpcionDeFeedback[];
  tipos: OpcionDeFeedback[];
  estados: OpcionDeFeedback[];
}

/** Quién escribió el comentario. Solo lo que la fila muestra. */
export interface AutorDelComentario {
  id: number;
  nombre: string;
}

export interface ComentarioDeFeedback {
  id: number;
  modulo: string;
  /** Rótulo del módulo, tal como lo escribe el backend. */
  modulo_etiqueta: string;
  tipo: string;
  /** Rótulo del tipo, tal como lo escribe el backend. */
  tipo_etiqueta: string;
  estado: string;
  /** Rótulo del estado, tal como lo escribe el backend. */
  estado_etiqueta: string;
  texto: string;
  autor: AutorDelComentario;
  /* Qué puede hacer QUIEN PIDE con ESTA fila. Los decide la Policy del
     servidor, que es la misma que autoriza cada request: por eso la pantalla
     no puede ofrecer un botón que después reciba un 403. Dibujan, no
     autorizan — la autorización real la sigue haciendo el backend. */
  puede_editar: boolean;
  puede_eliminar: boolean;
  puede_cambiar_estado: boolean;
  /** INSTANTE en UTC: se muestra con `formatearMomento`, no con `formatearFecha`. */
  created_at: string;
}

export interface NuevoComentario {
  modulo: string;
  tipo: string;
  texto: string;
}

/** Los tres filtros del tablero, todos opcionales y combinables. */
export interface FiltrosDeFeedback {
  modulo?: string;
  tipo?: string;
  autorId?: number;
  pagina?: number;
}

export interface PaginaDeFeedback {
  comentarios: ComentarioDeFeedback[];
  /** Cuántos quedaron DESPUÉS del filtro: el "de M" del pie. */
  total: number;
  /** Cuántos hay sin filtrar: sin esto, filtrar hasta cero se lee como tablero vacío. */
  totalGeneral: number;
  pagina: number;
  porPagina: number;
  totalPaginas: number;
}

interface RespuestaPaginada {
  data: ComentarioDeFeedback[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    total_general: number;
  };
}

/**
 * El tablero, lo último primero. Filtra, ordena y pagina el BACKEND; acá solo
 * se traducen los controles a query params.
 */
export async function fetchFeedback(
  filtros: FiltrosDeFeedback = {},
): Promise<PaginaDeFeedback> {
  const parametros = new URLSearchParams();
  // Solo lo que el usuario eligió: mandar `modulo=` vacío haría que el
  // backend lo tome por presente y lo valide contra el enum — 422 por un
  // filtro que nadie puso.
  if (filtros.modulo) parametros.set("modulo", filtros.modulo);
  if (filtros.tipo) parametros.set("tipo", filtros.tipo);
  if (filtros.autorId) parametros.set("autor_id", String(filtros.autorId));
  if (filtros.pagina && filtros.pagina > 1) parametros.set("page", String(filtros.pagina));

  const cadena = parametros.toString();
  const respuesta = await apiFetch<RespuestaPaginada>(
    `/api/feedback${cadena ? `?${cadena}` : ""}`,
  );

  return {
    comentarios: respuesta.data,
    total: respuesta.meta.total,
    totalGeneral: respuesta.meta.total_general,
    pagina: respuesta.meta.current_page,
    porPagina: respuesta.meta.per_page,
    totalPaginas: respuesta.meta.last_page,
  };
}

/** Las listas del formulario: módulos, tipos y estados. */
export function fetchCatalogosDeFeedback(): Promise<CatalogosDeFeedback> {
  // Sin envelope `data`: el endpoint devuelve un objeto plano con las listas,
  // igual que `/api/user`.
  return apiFetch<CatalogosDeFeedback>("/api/feedback/catalogos");
}

/**
 * Las personas que el filtro por autor ofrece: quienes efectivamente
 * escribieron algo.
 *
 * Endpoint aparte de los catálogos y no una clave más: aquellos son enums y
 * se cachean media hora, esta lista crece cada vez que alguien comenta por
 * primera vez. Juntas obligarían a elegir entre refrescar de más los enums o
 * mostrar un filtro al que le falta quien acaba de escribir.
 */
export async function fetchAutoresDeFeedback(): Promise<AutorDelComentario[]> {
  const respuesta = await apiFetch<{ data: AutorDelComentario[] }>("/api/feedback/autores");
  return respuesta.data;
}

/**
 * Deja un comentario. El autor NO viaja: lo pone el servidor a partir de la
 * sesión, así que nadie puede firmar por otro.
 */
export async function crearComentarioRequest(
  input: NuevoComentario,
): Promise<ComentarioDeFeedback> {
  const respuesta = await apiFetch<{ data: ComentarioDeFeedback }>("/api/feedback", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return respuesta.data;
}

/**
 * Corrige el contenido de un comentario. Solo el autor o un Super Admin: si
 * no, el servidor responde 403 antes de mirar el cuerpo.
 *
 * PUT y no PATCH: viajan los tres campos siempre. Con un PATCH, "no mandé el
 * módulo" y "quiero borrar el módulo" serían indistinguibles.
 */
export async function actualizarComentarioRequest(
  id: number,
  input: NuevoComentario,
): Promise<ComentarioDeFeedback> {
  const respuesta = await apiFetch<{ data: ComentarioDeFeedback }>(`/api/feedback/${id}`, {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return respuesta.data;
}

/** Borra el comentario. Mismo permiso que editar. Responde 204, sin cuerpo. */
export function eliminarComentarioRequest(id: number): Promise<void> {
  return apiFetch<void>(`/api/feedback/${id}`, { method: "DELETE" });
}

/**
 * Mueve el estado del comentario (pendiente → en desarrollo → desarrollado).
 * SOLO el Super Admin.
 *
 * Endpoint propio y no un campo más de la edición porque su permiso es otro:
 * el autor edita lo suyo, el estado no lo toca.
 */
export async function actualizarEstadoRequest(
  id: number,
  estado: string,
): Promise<ComentarioDeFeedback> {
  const respuesta = await apiFetch<{ data: ComentarioDeFeedback }>(
    `/api/feedback/${id}/estado`,
    { method: "PATCH", body: JSON.stringify({ estado }) },
  );
  return respuesta.data;
}
